import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import { Card, CardContent, Checkbox, Stack, useMediaQuery } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLoginUser, fetchSongReleaseList, setLoader, setSnackBar, setSnackBarMessage, setSnackBarVariant } from '../../redux';
import SnackbarContainer from '../Snackbar';
import { formSnackbar } from '../../_services/_constants/snackbar.constant';
import { getSongStatusBgColor } from '../../_helpers/reusablefunctions/getColor';
import { getDateAsPerFormat } from '../../_helpers/reusablefunctions/dateSetter';
import { submitSong } from '../../_services/submitAsong/submitAsong.service';
import { default_limit, record_limits } from '../../_services/_constants/recordLimitset.constant';
import { useHistory, useParams } from 'react-router-dom';
import defaultImage from '../../img/thumbnail.png';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: 'song_name',
    label: 'Song Name',
    width: 250,
    maxWidth: 300
  },
  {
    id: 'album',
    label: 'Album/File',
    width: 250,
    maxWidth: 300
  },
  {
    id: 'genre',
    label: 'Genre',
    width: 250,
    maxWidth: 300
  },
  {
    id: 'mood',
    label: 'Mood',
    width: 250,
    maxWidth: 300
  },
  {
    id: 'release_date',
    label: 'Release Date',
    width: 250,
    maxWidth: 300
  },
  {
    id: 'song_status',
    label: 'Status',
    width: 250,
    maxWidth: 300
  },
];

function EnhancedTableHead(props) {
  const {onSelectAllClick, order, orderBy,handleRequestSort,numSelected,rowCount, onRequestSort } = props;

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
      <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': 'select all',
            }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            onClick={createSortHandler(headCell.id)}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  rowCount: PropTypes.number.isRequired,
};

export default function SongTable({ songListData,handleChangePage,handleChangeRowsPerPage,page,rowsPerPage }) {
  const history = useHistory()
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('song_name');
  const [selected, setSelected] = useState([]);
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.userData.userData);
  const [rows, setRows] = useState(songListData && songListData.results?songListData.results:[]);
  const [totalCount, setTotalCount] = useState(songListData?songListData.total_count:0)
  const smallScreen = useMediaQuery('(max-width: 600px)');


  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => 
  {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.song_id);
      
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }

    setSelected(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const removeData = () => {
    if (selected.length !== undefined && selected.length === 1) {
      dispatch(setSnackBarVariant('info'));
      dispatch(setSnackBarMessage(formSnackbar.submitAsong.SongInfo));
      dispatch(setSnackBar(true));

      const itemId = selected;
      submitSong({ itemId: itemId }, 'DELETE')
        .then((response) => {
          if (response.status === 200 || response.status === 204) {
            dispatch(setSnackBarVariant('success'));
            dispatch(setSnackBarMessage(formSnackbar.submitAsong.SongInfoSuccess));
            dispatch(setLoader(false));
            setSelected([]);

            if (userData.id) {
              dispatch(fetchLoginUser(userData.id));
              dispatch(fetchSongReleaseList());
            }
          } else {
            dispatch(setSnackBarMessage(formSnackbar.serviceRequest.serviceRequestFailure));
            dispatch(setSnackBarVariant('error'));
          }
        })
        .catch((error) => {
          dispatch(setSnackBarMessage(formSnackbar.errors.errorMsg));
          dispatch(setSnackBarVariant('error'));
          return error.response;
        });
    }
  };

/*   useEffect(() => {
    if (userData.id) {
      dispatch(fetchLoginUser(userData.id));
      dispatch(fetchSongReleaseList());
    }
  }, [userData.id, dispatch]); */

  useEffect(() => {
    if (songListData && songListData.results && songListData.results.length > 0) {
      setRows(songListData.results);
      setTotalCount(songListData.total_count)
    }
  }, [songListData]);

  return (
    <div className="container-fluid py-4 creator-playlist-panel">
      <div className="row">
        <Stack style={{ height: '100%' }}>
          <Card className='mb-3' sx={{ minWidth: '100%', height: 'auto', boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.18)', borderRadius: '6px' }}>
            <CardContent sx={{ padding: '0px !important' }}>
              <TableContainer>
                <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
                  <EnhancedTableHead
                    numSelected={selected.length}
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={handleSelectAllClick}
                    onRequestSort={handleRequestSort}
                    rowCount={rows.length}
                  />
                  <TableBody>
                    {stableSort(rows, getComparator(order, orderBy))
                     
                      .map((row, index) => {
                        const isItemSelected = isSelected(row.song_id);
                        const labelId = `enhanced-table-checkbox-${index}`;

                        return (
                          <TableRow
                            hover
                            onClick={(event) => handleClick(event, row.song_id)}
                            role="checkbox"
                            aria-checked={isItemSelected}
                            tabIndex={-1}
                            key={row.song_id}
                            selected={isItemSelected}
                          >
                          <TableCell padding="checkbox" >
                            <Checkbox
                                color="primary"
                                checked={isItemSelected}
                                inputProps={{
                                'aria-labelledby': labelId,
                                }}
                            />
                            </TableCell>
                            <TableCell align="left">
                              <div className="d-flex align-items-center">
                                <div>
                                  <img
                                    className="rounded"
                                    src={row.cover_image ? process.env.REACT_APP_SONGAPIURL_MEDIA + row.cover_image : defaultImage}
                                    style={{ width: '40px', height: '40px' }}
                                    alt=""
                                  />
                                </div>
                                <div style={{ width: '10px' }}></div>
                                <div>
                                  <span style={{cursor:'pointer'}} onClick={()=>history.push(`/submitsong/${row.song_id}`)}>{row.song_name}</span>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell align="left">{row.album}</TableCell>
                            <TableCell align="left">{row.genre}</TableCell>
                            <TableCell align="left">{row.mood}</TableCell>
                            <TableCell align="left">{row.release_date &&getDateAsPerFormat(row.release_date, 'DD Mon, YYYY')}</TableCell>
                            <TableCell align="left">
                              <div className="status text-light" style={{ background: getSongStatusBgColor(row.song_status), minWidth: '80px' }}>
                                {row.song_status}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={record_limits}
                component="div"
                colSpan={3}
                sx={{
                  width: '100%',
                  padding: '10px 10px',
                  fontSize: smallScreen ? '0.75rem' : 'inherit',
                  overflowX: 'hidden',
                  marginLeft: smallScreen ? '0rem' :'inherit',
                }}
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={smallScreen? '' : "Rows per page"}
                SelectProps={{
                  inputProps: {
                    'aria-label': 'rows per page',
                  },
                  native: true,
                }}
              />
            </CardContent>
          </Card>
        </Stack>
      </div>
      <SnackbarContainer />
    </div>
  );
}

// SongTable.propTypes = {
//   songListData: PropTypes.array.isRequired,
// };
